import { Link } from "@tanstack/react-router";
import { usePublicTenantsQuery, PublicTenantWithProcedures } from "@/src/api/Tenant";
import { MapPin, Calendar, ChevronRight, Building2, Clock, Archive } from "lucide-react";
import { useMemo } from "react";
import { getProcedureStatus } from "@/src/lib/procedureStatus";

type Proc = PublicTenantWithProcedures["procedures"][number];

function HomePage() {
  const { data: tenantsRaw, isLoading, isError } = usePublicTenantsQuery();
  const tenants = Array.isArray(tenantsRaw) ? tenantsRaw : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="w-full px-6 md:px-12 py-12 md:py-16">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="h-8 w-8" />
            <span className="text-lg font-medium opacity-80">wniosekdoplanu.pl</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Portal składania wniosków do planów zagospodarowania przestrzennego
          </h1>
          <p className="text-lg opacity-90 max-w-2xl">
            Wybierz swoją gminę i złóż wniosek lub uwagę do planu ogólnego lub miejscowego planu
            zagospodarowania przestrzennego — bez rejestracji, bezpośrednio przez przeglądarkę.
          </p>
        </div>
      </div>

      {/* Main */}
      <div className="w-full px-6 md:px-12 py-10">
        <h2 className="text-xl font-bold text-gray-800 mb-6">
          <Building2 className="h-5 w-5 inline-block mr-2 -mt-0.5" />
          Gminy korzystające z systemu
        </h2>

        {isLoading && (
          <div className="text-center py-12 text-gray-400">Ładowanie listy gmin...</div>
        )}

        {isError && (
          <div className="text-center py-12 text-red-400">Nie udało się pobrać listy gmin. Spróbuj odświeżyć stronę.</div>
        )}

        {!isLoading && !isError && tenants.length === 0 && (
          <div className="text-center py-12 text-gray-400">Brak zarejestrowanych gmin.</div>
        )}

        {tenants.length > 0 && (
          <div className="space-y-8">
            {tenants.map((tenant) => (
              <TenantCard key={tenant.id} tenant={tenant} />
            ))}
          </div>
        )}

        {/* Admin / login */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500">
          <Link
            to="/admin/login"
            className="text-blue-600 hover:text-blue-800 underline">
            Panel administratora systemu
          </Link>
          <span className="hidden sm:inline">·</span>
          <span>
            Jesteś gminą i chcesz korzystać z systemu?{" "}
            <a className="underline text-blue-600" href="mailto:kontakt@wniosekdoplanu.pl">
              Napisz do nas
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}

function TenantCard({ tenant }: { tenant: PublicTenantWithProcedures }) {
  const { active, upcoming, archived } = useMemo(() => {
    const a: Proc[] = [];
    const u: Proc[] = [];
    const r: Proc[] = [];
    for (const p of tenant.procedures) {
      const cls = getProcedureStatus(p);
      if (cls === "active") a.push(p);
      else if (cls === "upcoming") u.push(p);
      else r.push(p);
    }
    return { active: a, upcoming: u, archived: r };
  }, [tenant.procedures]);

  const primaryColor = tenant.primaryColor || "#1d4ed8";
  const totalActive = active.length;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header gminy */}
      <Link
        to="/$tenant"
        params={{ tenant: tenant.id }}
        className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors"
        style={{ borderLeft: `4px solid ${primaryColor}` }}>
        {tenant.logo && (
          <img
            src={tenant.logo}
            alt={`Herb ${tenant.displayName}`}
            className="h-12 w-auto object-contain shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900">{tenant.displayName}</h3>
          <p className="text-sm text-gray-500">
            {totalActive > 0 ? `${totalActive} trwając${totalActive === 1 ? "a" : totalActive < 5 ? "e" : "ych"} procedur` : "Brak trwających procedur"}
            {upcoming.length > 0 && ` · ${upcoming.length} nadchodzące`}
            {archived.length > 0 && ` · ${archived.length} archiwalnych`}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
      </Link>

      {/* Lista procedur pogrupowana */}
      {tenant.procedures.length > 0 && (
        <div className="border-t border-gray-100">
          {active.length > 0 && (
            <ProcedureGroup
              tenantId={tenant.id}
              label="Trwające"
              icon={<MapPin className="h-3.5 w-3.5" />}
              procedures={active}
              badgeCls="bg-green-100 text-green-700"
              badgeLabel="Trwająca"
            />
          )}
          {upcoming.length > 0 && (
            <ProcedureGroup
              tenantId={tenant.id}
              label="Nadchodzące"
              icon={<Clock className="h-3.5 w-3.5" />}
              procedures={upcoming}
              badgeCls="bg-amber-100 text-amber-700"
              badgeLabel="Wkrótce"
            />
          )}
          {archived.length > 0 && (
            <ProcedureGroup
              tenantId={tenant.id}
              label="Archiwalne"
              icon={<Archive className="h-3.5 w-3.5" />}
              procedures={archived}
              badgeCls="bg-gray-100 text-gray-500"
              badgeLabel="Archiwalna"
            />
          )}
        </div>
      )}
    </div>
  );
}

function ProcedureGroup({
  tenantId,
  label,
  icon,
  procedures,
  badgeCls,
  badgeLabel,
}: {
  tenantId: string;
  label: string;
  icon: React.ReactNode;
  procedures: Proc[];
  badgeCls: string;
  badgeLabel: string;
}) {
  return (
    <div className="px-5 py-3 border-b border-gray-50 last:border-b-0">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-gray-400">{icon}</span>
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          {label} ({procedures.length})
        </span>
      </div>
      <div className="space-y-1">
        {procedures.map((proc) => (
          <ProcedureRow key={proc.id} tenantId={tenantId} proc={proc} badgeCls={badgeCls} badgeLabel={badgeLabel} />
        ))}
      </div>
    </div>
  );
}

function ProcedureRow({
  tenantId,
  proc,
  badgeCls,
  badgeLabel,
}: {
  tenantId: string;
  proc: Proc;
  badgeCls: string;
  badgeLabel: string;
}) {
  const from = new Date(proc.activeFrom).toLocaleDateString("pl-PL");
  const to = new Date(proc.activeTo).toLocaleDateString("pl-PL");

  return (
    <Link
      to="/$tenant/$procedureSlug"
      params={{ tenant: tenantId, procedureSlug: proc.name }}
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-blue-50 transition-colors group">
      <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded ${badgeCls}`}>
        {badgeLabel}
      </span>
      <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 truncate group-hover:text-blue-700">
        {proc.displayName}
      </span>
      <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
        <Calendar className="h-3 w-3" />
        {from} — {to}
      </span>
      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-500 shrink-0" />
    </Link>
  );
}

export default HomePage;
